import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { AppstoreOutlined, DollarOutlined, FormOutlined } from '@ant-design/icons';
import { Menu, MenuProps } from 'antd';

import { useAuthorization } from 'entities/user/hooks/useAuthorization';
import { useInvoiceStatus } from 'features/navigation/model/useInvoiceStatus';
import { MyBills } from 'features/show-invoices/ui/MyInvoices';
import { MySubscriptions } from 'features/show-subscriptions/ui/MySubscriptions';
import { MyApplications } from 'pages/my-applications/ui/MyApplications/MyApplications';
import { BadgeStyled } from 'pages/subscriptions-management/ui/SubscriptionsManagement/SubscriptionsManagement.styles';
import { updateMyPage } from 'shared/lib/myPageSlice';
import { useAppDispatch, useAppSelector } from 'shared/lib/reduxHooks';
import { useScrollToTop } from 'shared/lib/useScrollToTop';
import {
	ButtonWithBadgeStyled,
	MenuContainerStyled,
	MyAppsAndSubscriptionsStyled,
} from './MyAppsAndSubscriptions.styles';

export const MyAppsAndSubscriptions: FC = () => {
	const { t } = useTranslation();
	const invoiceStatus = useInvoiceStatus();
	const authorization = useAuthorization();
	const canPurchaseApps = authorization?.canPurchaseApps;
	const myPage = useAppSelector(state => state.myPage.myPage);
	const dispatch = useAppDispatch();
	const history = useHistory();
	const { search } = useLocation();
	const searchParams = new URLSearchParams(search);

	useEffect(() => {
		const myPageQueryParam = searchParams.get(`tab`);
		if (myPageQueryParam !== `apps`) {
			dispatch(
				updateMyPage({
					myPage: myPageQueryParam ?? `apps`,
				}),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		history.push({ search: `tab=${myPage}` });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [myPage]);

	useScrollToTop();

	const items: MenuProps['items'] = [
		{
			label: t(`Marketplace_My_Apps`),
			key: `apps`,
			icon: <AppstoreOutlined />,
		},
		{
			label: t(`Marketplace_Subscription_Management`),
			key: `subscriptions`,
			icon: <FormOutlined />,
		},
	];

	if (canPurchaseApps) {
		items.push({
			label: (
				<ButtonWithBadgeStyled>
					<p style={{ margin: 0, position: `relative`, width: `fit-content` }}>
						{t(`Marketplace_My_Bills`)}
						{!!invoiceStatus && <BadgeStyled color='error' />}
					</p>
				</ButtonWithBadgeStyled>
			),
			key: `invoices`,
			icon: <DollarOutlined />,
		});
	}

	const onClick: MenuProps['onClick'] = e => {
		dispatch(
			updateMyPage({
				myPage: e.key,
			}),
		);
	};

	return (
		<MyAppsAndSubscriptionsStyled>
			<MenuContainerStyled>
				<Menu
					mode='inline'
					items={items}
					selectedKeys={[myPage]}
					inlineIndent={8}
					onClick={onClick}
					style={{ border: `none` }}
				/>
			</MenuContainerStyled>
			{myPage === `apps` && <MyApplications />}
			{myPage === `subscriptions` && <MySubscriptions />}
			{myPage === `invoices` && <MyBills />}
		</MyAppsAndSubscriptionsStyled>
	);
};
